import { AsyncPipe, Location } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    OnChanges,
    OnInit,
    output,
    SimpleChanges,
    viewChild,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CheckboxChangeEventDetail, IonCardContent, ModalController } from '@ionic/angular';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { format, formatDate, parseISO } from 'date-fns';
import { lastValueFrom, Observable, of } from 'rxjs';
import { Subscription } from 'rxjs/internal/Subscription';
import { debounceTime } from 'rxjs/operators';
import { User } from '../../entities/user';
import { mainUserRoles, UserRoles } from '../../entities/user-roles';
import { AccountType } from '../../entities/account-type';
import { LoginUserWithoutPasswordDto } from '../../entities/login-user-without-password-dto';
import { PlainUserDto } from '../../entities/plain-user-dto';
import { UpdateUserDto } from '../../entities/update-user-dto';
import {
    DisplayOptionsSchema,
    DisplayOptionTypes,
    SelectDisplayOptions,
    UserCustomProperty,
    UserCustomPropertyDto,
    UserCustomPropertySchema,
} from '../../entities/user-custom-property';
import { CurafidaAuthService } from '../../../authentication/curafida-auth.service';
import {
    CurafidaTextInputComponent,
    InputMode,
} from '../../../common/components/curafida-input/curafida-text-input/curafida-text-input.component';
import { UserFormComponent } from '../../../common/components/user-form.component';
import { ICardActionButton } from '../../../common/entities/card-action-button.interface';
import { ButtonConfig } from '../../../common/entities/modal/modal-button';
import { ModalConfig } from '../../../common/entities/modal/modal-config';
import { ModalTyp } from '../../../common/entities/modal/modal-typ';
import { PaginatedResponse } from '../../../common/entities/paginated-response';
import { RoutingSegment } from '../../../common/entities/routing-segment';
import { IonicColor } from '../../../common/entities/toast/ionic-color';
import { FormGroupBackup } from '../../../common/form-group.backup';
import { LoadingService } from '../../../common/services/loading/loading.service';
import { ModalAlertService } from '../../../common/services/modal';
import { StyleService } from '../../../common/services/style/style.service';
import { ToastService } from '../../../common/services/toast-service/toast-service.service';
import {
    defaultInputValidator,
    noWhitespaceValidator,
    repeatValidationValidator,
    requiredUserNameValidators,
    requiredValidators,
} from '../../../common/validators/curafida-validators';
import { ConfigService } from '../../../config/services/config.service';
import { RoutingService } from '../../../config/services/routing.service';
import { HypermediaResource } from '../../../hateoas/hateoas.model';
import { LoggingService } from '../../../logging/logging.service';
import { CheckBoxItemAdapterComponent } from '../../../table/components/table-adapter/checkbox-item-adapter.component';
import { StringItemAdapterComponent } from '../../../table/components/table-adapter/string-item-adapter.component';
import { ItemType, TableConfig, TableItem } from '../../../table/entities/table';
import { Content } from '../../../therapy/entities/content';
import { Group, GroupCategory, GroupLevel } from '../../entities/group';
import { GroupListItem } from '../../entities/group-list.item';
import { GroupService } from '../../services/group';
import { PatientAccountService } from '../../services/patient-account.service';
import { UsersService } from '../../services/user/users.service';
import { UserCustomPropertyService } from '../../services/user-custom-property.service';
import { GroupListModalComponent } from '../group-list-modal/group-list-modal.component';
import { UserContactComponent } from '../user-contact/user-contact.component';
import { UserRoleCardComponent } from '../user-role-card/user-role-card.component';
import { UserTagViewComponent } from '../user-tag-view/user-tag-view.component';
import { academicTitleOptions } from './academic-title.options';
import { UserMainInfoConfig } from './entities/user-main-info-config';
import { CurafidaCardComponent } from '../../../common/components/curafida-card/curafida-card.component';
import { CurafidaRadioButtonInputComponent } from '../../../common/components/curafida-input/curafida-radio-button-input/curafida-radio-button-input.component';
import {
    CurafidaSelectInputComponent,
    SelectInputItem,
} from '../../../common/components/curafida-input/curafida-select-input/curafida-select-input.component';
import { CurafidaDateInputComponent } from '../../../common/components/curafida-input/curafida-date-input/curafida-date-input.component';
import { CurafidaSaveCancelButtonComponent } from '../../../common/components/curafida-save-cancel-button/curafida-save-cancel-button.component';
import { AuthorizationPipe, NoAuthorizationPipe } from '../../../hateoas/authorization.pipe';
import { UserTagFilterPipe } from '../../pipes/user-tag-filter.pipe';
import { PatientCareManagementComponent } from './patient-care-management/patient-care-management.component';
import { CurafidaTableComponent } from '../../../table/components/curafida-table/curafida-table.component';
import { CurafidaContentInputComponent } from '../../../common/components/curafida-input/curafida-content-input/curafida-content-input.component';
import { CurafidaContentInputForm } from '../../../common/components/curafida-input/curafida-content-input/curafida-content-input.form';
import { CurafidaCheckboxInputComponent } from '../../../common/components/curafida-input/curafida-checkbox-input/curafida-checkbox-input.component';
import { UserPreferencesComponent } from '../../../config/components/user-preferences.component/user-preferences.component';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'user-main-info',
    templateUrl: './user-main-info.component.html',
    styleUrls: ['./user-main-info.component.scss'],
    imports: [
        UserTagViewComponent,
        ReactiveFormsModule,
        CurafidaCardComponent,
        CurafidaTextInputComponent,
        CurafidaRadioButtonInputComponent,
        CurafidaSelectInputComponent,
        AsyncPipe,
        CurafidaDateInputComponent,
        CurafidaSaveCancelButtonComponent,
        IonCardContent,
        TranslatePipe,
        PatientCareManagementComponent,
        NoAuthorizationPipe,
        UserRoleCardComponent,
        CurafidaTableComponent,
        CurafidaContentInputComponent,
        CurafidaCheckboxInputComponent,
        UserContactComponent,
        UserPreferencesComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [UserTagFilterPipe, NoAuthorizationPipe, AuthorizationPipe],
})
export class UserMainInfoComponent extends UserFormComponent implements OnInit, OnChanges {
    protected readonly modalController = inject(ModalController);
    public readonly router = inject(Router);
    private readonly modalAlertService = inject(ModalAlertService);
    public readonly route = inject(ActivatedRoute);
    protected readonly formBuilder = inject(FormBuilder);
    private readonly styleService = inject(StyleService);
    protected readonly usersService = inject(UsersService);
    protected readonly groupService = inject(GroupService);
    private readonly toastService = inject(ToastService);
    private readonly authService = inject(CurafidaAuthService);
    public readonly configService = inject(ConfigService);
    private readonly loadingService = inject(LoadingService);
    private readonly location = inject(Location);
    public readonly routingService = inject(RoutingService);
    private readonly httpClient = inject(HttpClient);
    private readonly userCustomPropertyService = inject(UserCustomPropertyService);
    protected readonly patientAccountService = inject(PatientAccountService);
    private readonly translateService = inject(TranslateService);

    readonly userRoleCardComponent = viewChild<UserRoleCardComponent>('UserRoleCardComponent');
    readonly userContactComponent = viewChild(UserContactComponent);
    readonly academicTitleOptions = academicTitleOptions();

    patientDossier$: Observable<HypermediaResource>;
    forceMobileDesign = input<boolean>(false);
    readonly config = input<UserMainInfoConfig>(new UserMainInfoConfig());
    readonly mainUserRole = input<UserRoles>();
    userChange = output<User>();

    readonly updateTitle = output<{ uuid: string; firstName: string; lastName: string; birthDate: string }>();
    usernameSuggestion: Subscription;
    existingUsernameSubscription: Subscription;
    usePseudonym = [
        { value: false, label: 'Deaktiviert' },
        {
            value: true,
            label: 'Aktiviert für Formulare',
            subtitle:
                '(Anstelle von personenbezogenen Daten wird ausschließlich das Pseudonym in Formularen verwendet)',
        },
    ];
    pseudonymDefinition = [
        { value: true, label: 'Identisch zu Benutzer-ID' },
        { value: false, label: 'Manuelle Eingabe' },
    ];
    isEditEnabled = false;
    queryParamsSubscription: Subscription;
    isPatientForConsultation: boolean;
    isPatientForScreening: boolean;
    isPatientForOrder: boolean;
    userForm: FormGroup;
    userFormBackup: FormGroupBackup;
    isNewUser: boolean;
    userMemberships: string[] = [];
    RoutingSegment = RoutingSegment;
    UserRoles = UserRoles;
    AccountType = AccountType;
    originalAccountType: AccountType;
    originallyHadOtpConfigured: boolean;
    otpConfigurationList = [
        { value: false, label: '2FA.DEACTIVATED' },
        { value: true, label: '2FA.ACTIVATED' },
    ];

    groupListConfig: TableConfig<GroupListItem[]> = new TableConfig<GroupListItem[]>();
    onlyOneGroupAvailable: boolean;
    isGroupManager: boolean;
    InputMode = InputMode;
    // This flag exists to avoid sending the pseudonym in the updateUserDto if it has not been changed from the original pseudonym
    // Otherwise the backend responds with an error, because the pseudonym already exists #4189
    // Not relevant for new users
    updatePseudonym = false;

    customPropertySchemas: UserCustomPropertySchema[] = [];
    customPropertyFileTableConfigs: Record<string, TableConfig<Content[]>> = {};
    /** Keeps each custom property's validators and value in sync with its display condition. */
    customPropertyVisibilitySubscription: Subscription;
    pendingCustomProperties: UserCustomProperty[] = [];
    customPropertyFileForms: Record<string, CurafidaContentInputForm> = {};
    fileDeletionQueue: string[] = [];

    groupActionButtons: ICardActionButton[] = [];
    accountTypesSelection: { value: string; label: string }[] = [
        {
            value: AccountType.LOGIN_USER,
            label: `ACCOUNT_TYPE.${AccountType.LOGIN_USER}`,
        },
        {
            value: AccountType.PLAIN_USER,
            label: `ACCOUNT_TYPE.${AccountType.PLAIN_USER}`,
        },
    ];
    readonly isMobile = computed(() => {
        if (this.forceMobileDesign()) {
            return true;
        } else {
            return this.styleService.isMobile();
        }
    });
    protected readonly log = inject(LoggingService).getLogger(this.constructor.name);
    /** The patient this form handed out last, so it is not taken back in as one handed over anew */
    private emittedUser: User | undefined;

    constructor() {
        super();
        this.isNewUser = this.route.snapshot.paramMap.get('userUuid') === 'new';
    }

    /**
     * Shows a patient handed over anew from outside, e.g. once its master data, tags or Betreuer were changed in the
     * side panel, unless it is the one this form emitted itself. Its Betreuer and Co-Betreuer are loaded anew, its master
     * data only while the form is not being edited.
     */
    ngOnChanges(changes: SimpleChanges): void {
        const change = changes['user'];
        if (!change || change.firstChange || !this.userForm) return;
        if (change.currentValue === this.emittedUser) return;
        this.refreshPatientDossier();
        if (!this.isEditEnabled) this.showMasterDataOf(this.user);
    }

    /**
     * Hands out the reloaded patient once its tags were saved or a Betreuer or Co-Betreuer was added or removed, so
     * the other views of the patient, e.g. the side panel, show the change as well
     */
    async emitReloadedUser(): Promise<void> {
        try {
            this.user = await this.usersService.getUser(this.user.username);
            this.emitUser();
        } catch (error) {
            this.log.error('Error in emitReloadedUser', error);
        }
    }

    async ngOnInit(): Promise<void> {
        this.log.info('[ngOnInit] start');
        console.time('ngOnInit');
        try {
            this.loggedInUser = this.authService.getSession().user;
            this.isGroupManager = this.usersService.isUserGroupManager(this.loggedInUser);
            this.groupActionButtons.push({
                icon: 'add',
                id: 'add-group',
                isDisabled: false,
                isHidden: false,
                isIconButton: this.isMobile(),
                title: 'USER.ASSIGN_GROUP',
            });
            this.queryParamsSubscription = this.route.queryParams.subscribe((params) => {
                this.isPatientForConsultation = !!params?.isPatientForConsultation;
                this.isPatientForScreening = !!params?.isPatientForScreening;
                this.isPatientForOrder = !!params?.isPatientForOrder;
            });
            this.onlyOneGroupAvailable =
                this.loggedInUser.groups?.length === 1 &&
                this.usersService.isUserMemberOfLowestLevel(this.loggedInUser);
            await this.initUserForm();
            if (this.route.snapshot.paramMap.get('userUuid') === 'new') {
                this.user = this.usersService.initUserDependingOnUrl(this.getLocation());
                this.emitUser();
                this.isEditEnabled = true;

                // TODO: write custom async validator
                this.usernameSuggestion = this.usersService
                    .getUsernameSuggestion(this.userForm, 'firstName', 'lastName')
                    .subscribe((value) => this.userForm.patchValue({ username: value }));

                // TODO: switchMap cancel requests
                this.existingUsernameSubscription = this.userForm.controls.username.valueChanges
                    .pipe(debounceTime(400))
                    .subscribe(async (value) => {
                        if (this.userForm.controls.username.dirty && this.userForm.controls.username.valid) {
                            const usernameCheck = await this.usersService.isUsername(value);
                            if (usernameCheck.isUsername) {
                                this.userForm.controls.username.setErrors({ incorrect: true });
                            }
                        }
                    });
                if (this.hasRole()) {
                    if (
                        this.loggedInUser?.roles?.includes(UserRoles.create_patient) ||
                        this.loggedInUser?.roles?.includes(UserRoles.create_supervised_patient)
                    ) {
                        this.patientAccountService.patientAccountCreationConfig$.subscribe((it) => {
                            this.user.accountType = it.defaultAccountType;
                            this.onChangeAccountType(this.user.accountType);
                        });
                    }
                } else {
                    this.user.accountType = AccountType.LOGIN_USER;
                }
            }

            if (this.configService.frontendConfig().features.organisation?.enabled) {
                await this.initGroupsConfig();
            }
            this.refreshPatientDossier();
        } catch (err) {
            this.log.error('[ngOnInit] error');
            this.log.error(err);
        } finally {
            console.timeEnd('ngOnInit');
            this.log.info('[ngOnInit] end');
        }
    }

    private refreshPatientDossier(): void {
        if (this.isPatientCareManagementActive()) {
            this.patientDossier$ = this.httpClient.get<HypermediaResource>(`patients/${this.user.username}/dossier`);
        } else {
            this.patientDossier$ = of({ _links: {} });
        }
    }

    isPatientCareManagementActive(): boolean {
        /* `features.user` is typed as required but the backend deletes it from the unauthenticated
         * `configuration/public` response (@ConfigAuthFilter('patient-and-privileged')), and that
         * response gets cached in localStorage and re-applied on startup — so it can be absent. */
        const userFeatures = this.configService.config()?.configuration?.features?.user;
        return !!(
            (userFeatures?.caregiver?.enabled || userFeatures?.supervisor?.enabled) &&
            this.user?.roles?.includes(UserRoles.PATIENT) &&
            !this.isProfilePage() &&
            !this.isNewUser &&
            this.config().patientCareManagement
        );
    }

    hasRightToModifyUser(): boolean {
        if (this.user?.roles?.includes(UserRoles.PATIENT) || this.readUserRoleFromURL() === UserRoles.PATIENT) {
            return this.loggedInUser.roles.includes(UserRoles.manage_patient);
        }
        return this.loggedInUser.roles.includes(UserRoles.manage_user);
    }

    /**
     * Returns false for new users or if updating another user. True if updating the own user details.
     */
    isOwnDetails(): boolean {
        return this.user?.username === this.loggedInUser?.username;
    }

    async initGroupList(): Promise<void> {
        // If it is an existing user, show the group of which it is member
        if (!this.isNewUser) {
            try {
                this.groupListConfig.list = (await this.usersService.getGroupsOfUser(
                    this.user.username,
                    null,
                    null,
                    null,
                    null,
                    null,
                    null,
                    null,
                    false,
                )) as PaginatedResponse<GroupListItem[]>;
                // Set extra attributes to show in the table
                for (const group of this.groupListConfig.list.items) {
                    const groupPath = group.path.split('/');
                    const groupLevel = this.groupService.getGroupLevel(group.path);
                    if (groupLevel === GroupLevel.TENANT || groupLevel === GroupLevel.ORGANIZATION) {
                        group.parentName = '-';
                        if (groupLevel === GroupLevel.TENANT) group.category = GroupCategory.TENANT;
                    } else {
                        group.parentName = groupPath[groupPath.length - 2];
                    }
                    group.titleName =
                        group.parentName === '-' ? `${group.name}` : `${group.name} (${group.parentName})`;
                    this.userMemberships.push(group.path);
                }
            } catch (err) {
                this.log.error('Error in initGroupList', err);
            }
            // If it's a new user, show the loggedInUser group by default
        } else {
            try {
                this.groupListConfig.list = (await this.usersService.getGroupsOfUser(
                    this.loggedInUser.username,
                    null,
                    null,
                    null,
                    null,
                    null,
                    null,
                    null,
                    false,
                    GroupLevel.INSTITUTION,
                )) as PaginatedResponse<GroupListItem[]>;
                // If it's only one group, show by default
                // Empty the list if there's more than one group
                if (this.groupListConfig.list.items.length !== 1) {
                    this.groupListConfig.list = PaginatedResponse.init([]);
                }
                for (const group of this.groupListConfig.list.items) {
                    const groupPath = group.path.split('/');
                    const groupLevel = this.groupService.getGroupLevel(group.path);
                    if (groupLevel === GroupLevel.TENANT || groupLevel === GroupLevel.ORGANIZATION) {
                        group.parentName = '-';
                        if (groupLevel === GroupLevel.TENANT) group.category = GroupCategory.TENANT;
                    } else {
                        group.parentName = groupPath[groupPath.length - 2];
                    }
                    group.titleName =
                        group.parentName === '-' ? `${group.name}` : `${group.name} (${group.parentName})`;
                    this.userMemberships.push(group.path);
                }
            } catch (err) {
                this.log.error('Error in initGroupList', err);
            }
        }
    }

    readValuesFromForm(): void {
        this.user.title = this.userForm.controls.title.value;
        this.user.username = this.userForm.controls.username.value;
        this.user.firstname = this.userForm.value.firstName;
        this.user.lastname = this.userForm.value.lastName;
        // This flag is set to avoid sending the pseudonym in the updateUserDto if it has not been changed from the original pseudonym
        // Otherwise the backend responds with an error, because the pseudonym already exists #4189
        // Not relevant for new users
        if (this.user.pseudonym !== this.userForm.value.pseudonym) {
            this.updatePseudonym = true;
        }
        this.user.pseudonym = this.userForm.value.pseudonym === '' ? null : this.userForm.value.pseudonym;
        this.user.usePseudonym = this.user.pseudonym?.length >= 3 ? this.userForm.value.usePseudonym : false;
        this.user.accountType = this.userForm.value.accountType;
        this.user.birthdate = this.userForm.value.birthDate ? this.userForm.value.birthDate : null;
        this.user.streetAddress = this.userForm.value.streetAddress;
        this.user.postalCode = this.userForm.value.postalCode;
        this.user.city = this.userForm.value.city;
        this.user.country = this.userForm.value.country;
        const contact = this.userForm.get('contact') as FormGroup;
        if (contact) {
            this.user.email = contact.value.email;
            this.user.phone = contact.value.phone;
            this.user.mobilePhone = contact.value.mobilePhone;
            if (contact.value.company) {
                this.user.company = contact.value.company;
            }
        }
        this.user.gender = this.userForm.value.gender;
        if (this.userForm.value.job) this.user.job = this.userForm.value.job;
        if (this.userForm.value.patientNumber) this.user.patientNumber = this.userForm.value.patientNumber;
        if (this.userForm.value.pseudonymDefinition as boolean) {
            this.user.pseudonym = this.userForm.value.patientNumber;
        } else {
            this.user.pseudonym = this.userForm.value.pseudonym;
        }
    }

    async saveUserInformation(): Promise<void> {
        if (this.userForm.valid) {
            const userContactComponent = this.userContactComponent();
            if (userContactComponent.isSaveNecessary) {
                await userContactComponent.save();
            }
            this.readValuesFromForm();
            if (this.isNewUser) {
                await this.saveNewUser();
            } else {
                await this.updateExistingUser();
            }
            this.userFormBackup.create();
        } else {
            this.log.error('[saveUserInformation] invalid');
            this.toastService.showToast('Speichern nicht möglich. Bitte prüfen sie das Formular.', IonicColor.danger);
        }
    }

    async getUserHasOtpConfiguredOrRequired(): Promise<boolean> {
        if (!this.user?.username || this.user?.accountType !== AccountType.LOGIN_USER) {
            return false;
        }
        return await this.usersService.getUserHasOtpConfiguredOrRequired(this.user.username);
    }

    async setRequireOtpConfiguration(username: string): Promise<void> {
        await this.usersService.setRequireOtpConfiguration(username, true, !this.user.emailVerified);
    }

    async cancelEdit(): Promise<void> {
        if (this.isEditEnabled) {
            this.toggleEdit();
            this.userFormBackup.restore();
            this.resetPendingCustomProperties();
        }
    }

    async initUserForm(): Promise<void> {
        this.log.info('[initUserForm] start');
        try {
            if (!this.loggedInUser.roles.includes(UserRoles.PATIENT)) {
                this.patientAccountService.fetchAccountCreationConfig();
            }
            this.isEditEnabled = this.user == null;
            const userName = this.user?.username || '';
            const isPatientFromUrl = this.readUserRoleFromURL() === UserRoles.PATIENT;
            const accountType = this.user?.accountType ?? (isPatientFromUrl ? null : AccountType.LOGIN_USER);
            if (!isPatientFromUrl) {
                this.accountTypesSelection = [
                    {
                        value: AccountType.LOGIN_USER,
                        label: `ACCOUNT_TYPE.${AccountType.LOGIN_USER}`,
                    },
                ];
            }
            this.originalAccountType = accountType;
            const {
                pvsId,
                title,
                firstName,
                lastName,
                birthDate,
                pseudonym,
                usePseudonym,
                gender,
                streetAddress,
                postalCode,
                city,
                country,
                job,
                createdAt,
                patientNumber,
                pseudonymDefinition,
            } = this.masterDataOf(this.user);
            const hasOtpConfigured = await this.getUserHasOtpConfiguredOrRequired();
            this.originallyHadOtpConfigured = hasOtpConfigured;
            this.userForm = this.formBuilder.group({
                username: new FormControl(
                    {
                        value: userName,
                        disabled: true,
                    },
                    requiredValidators,
                ),
                accountType: new FormControl(
                    {
                        value: accountType,
                        disabled:
                            !this.isEditEnabled ||
                            accountType === AccountType.LOGIN_USER ||
                            !this.hasRightToModifyUser(),
                    },
                    requiredValidators,
                ),
                hasOtpConfigured: new FormControl({
                    value: hasOtpConfigured,
                    disabled:
                        hasOtpConfigured ||
                        !this.isEditEnabled ||
                        accountType !== AccountType.LOGIN_USER ||
                        !this.hasRightToModifyUser(),
                }),
                pvsId: new FormControl({ value: pvsId, disabled: !this.isEditEnabled }, defaultInputValidator),
                title: new FormControl({ value: title, disabled: !this.isEditEnabled }, defaultInputValidator),
                firstName: new FormControl(
                    { value: firstName, disabled: !this.isEditEnabled },
                    requiredUserNameValidators,
                ),
                lastName: new FormControl(
                    { value: lastName, disabled: !this.isEditEnabled },
                    requiredUserNameValidators,
                ),
                pseudonym: new FormControl({ value: pseudonym, disabled: true }, [
                    Validators.minLength(3),
                    Validators.pattern(/^([A-Z]|[0-9]|_|-|\.){3,50}$/i),
                ]),
                usePseudonym: new FormControl({ value: usePseudonym, disabled: !this.isEditEnabled }),
                birthDate: new FormControl({ value: birthDate, disabled: !this.isEditEnabled }, defaultInputValidator),
                gender: new FormControl({ value: gender, disabled: !this.isEditEnabled }, defaultInputValidator),
                streetAddress: new FormControl(
                    { value: streetAddress, disabled: !this.isEditEnabled },
                    defaultInputValidator,
                ),
                postalCode: new FormControl(
                    { value: postalCode, disabled: !this.isEditEnabled },
                    defaultInputValidator,
                ),
                city: new FormControl({ value: city, disabled: !this.isEditEnabled }, defaultInputValidator),
                country: new FormControl({ value: country, disabled: !this.isEditEnabled }, defaultInputValidator),
                job: new FormControl({ value: job, disabled: !this.isEditEnabled }, defaultInputValidator),
                createdAt: new FormControl({
                    value: createdAt,
                    disabled: true,
                }),
                patientNumber: new FormControl({
                    value: patientNumber,
                    disabled: true,
                }),
                pseudonymDefinition: new FormControl({
                    value: pseudonymDefinition,
                    disabled: true,
                }),
            });
            this.userForm.controls.username.setValidators(
                Validators.compose([
                    defaultInputValidator,
                    noWhitespaceValidator,
                    Validators.minLength(5),
                    Validators.maxLength(50),
                    Validators.required,
                    Validators.pattern(/^([A-Z]|[0-9]|_|-|\.){5,50}$/i),
                ]),
            );
            await this.initCustomPropsFormControls();
            this.userFormBackup = new FormGroupBackup(this.userForm);
            this.userFormBackup.create();
        } catch (err) {
            this.log.error('[initUserForm] error');
            this.log.error(err);
        } finally {
            this.log.info('[initUserForm] end');
        }
    }

    buildUpdateUserDto(): UpdateUserDto {
        const updateUserDto = new UpdateUserDto();
        updateUserDto.title = this.userForm.value.title;
        updateUserDto.firstname = this.userForm.value.firstName;
        updateUserDto.lastname = this.userForm.value.lastName;
        // This flag is used to avoid sending the pseudonym in the updateUserDto if it has not been changed from the original pseudonym
        // Otherwise the backend responds with an error, because the pseudonym already exists #4189
        // Not relevant for new users
        if (this.updatePseudonym) {
            updateUserDto.pseudonym = this.userForm.value.pseudonym === '' ? null : this.userForm.value.pseudonym;
            this.updatePseudonym = false;
        }
        if (this.userForm.value.pseudonymDefinition) {
            updateUserDto.usePseudonym = true;
            updateUserDto.pseudonym = this.user.patientNumber?.toString();
        } else {
            updateUserDto.usePseudonym =
                this.userForm.value.pseudonym?.length >= 3 ? this.userForm.value.usePseudonym : false;
        }
        updateUserDto.birthdate = this.userForm.value.birthDate
            ? formatDate(this.userForm.value.birthDate, 'yyyy-MM-dd')
            : null;
        if (this.userForm.value.disabled === 'true') {
            updateUserDto.disabled = true;
        } else if (this.userForm.value.disabled === 'false') {
            updateUserDto.disabled = false;
        }
        updateUserDto.streetAddress = this.userForm.value.streetAddress;
        updateUserDto.postalCode = this.userForm.value.postalCode;
        updateUserDto.city = this.userForm.value.city;
        updateUserDto.gender = this.userForm.value.gender;
        updateUserDto.country = this.userForm.value.country;
        updateUserDto.phone = this.userForm.value.phone;
        updateUserDto.mobilePhone = this.userForm.value.mobilePhone;
        const contact = this.userForm.get('contact') as FormGroup;
        if (contact) {
            updateUserDto.phone = contact.value.phone;
            updateUserDto.mobilePhone = contact.value.mobilePhone;
            if (contact.value.company) {
                updateUserDto.company = contact.value.company;
            }
            if (this.user.emailVerified === false && contact.controls.email.dirty) {
                // Send email address to directly update if old one not already verified and changed
                updateUserDto.email = contact.value.email;
            }
        }
        updateUserDto.gender = this.userForm.value.gender;
        if (this.userForm.value.job) updateUserDto.job = this.userForm.value.job;
        if (this.userForm.value.company) updateUserDto.company = this.userForm.value.company;

        const customProps = this.readCustomPropsFromForm();
        if (customProps?.length > 0) updateUserDto.customPropertyDtos = customProps;

        return updateUserDto;
    }

    buildLoginUserWithoutPasswordDto(): LoginUserWithoutPasswordDto {
        const loginUserDto = new LoginUserWithoutPasswordDto();
        loginUserDto.title = this.userForm.controls.title.value;
        loginUserDto.username = this.userForm.controls.username.value;
        loginUserDto.firstname = this.userForm.value.firstName;
        loginUserDto.lastname = this.userForm.value.lastName;
        loginUserDto.pseudonym = this.userForm.value.pseudonym === '' ? null : this.userForm.value.pseudonym;
        loginUserDto.usePseudonym = loginUserDto.pseudonym?.length >= 3 ? this.userForm.value.usePseudonym : false;
        const contact = this.userForm.get('contact') as FormGroup;
        if (contact) {
            loginUserDto.email = contact.value.email;
            loginUserDto.phone = contact.value.phone;
            loginUserDto.mobilePhone = contact.value.mobilePhone;
            if (contact.value.company) {
                loginUserDto.company = contact.value.company;
            }
        }
        loginUserDto.accountType = AccountType.LOGIN_USER;
        loginUserDto.birthdate = this.userForm.value.birthDate ? this.userForm.value.birthDate : null;
        loginUserDto.streetAddress = this.userForm.value.streetAddress;
        loginUserDto.postalCode = this.userForm.value.postalCode;
        loginUserDto.city = this.userForm.value.city;
        loginUserDto.country = this.userForm.value.country;
        loginUserDto.gender = this.userForm.value.gender;
        if (this.userForm.value.job) loginUserDto.job = this.userForm.value.job;
        if (this.userForm.value.company) loginUserDto.company = this.userForm.value.company;

        const customProps = this.readCustomPropsFromForm();
        if (customProps?.length > 0) loginUserDto.customPropertyDtos = customProps;

        return loginUserDto;
    }

    buildPlainUserDto(): PlainUserDto {
        const plainUserDto = new PlainUserDto();
        plainUserDto.accountType = AccountType.PLAIN_USER;
        plainUserDto.title = this.userForm.controls.title.value;
        plainUserDto.username = this.userForm.controls.username.value;
        plainUserDto.firstname = this.userForm.value.firstName;
        plainUserDto.lastname = this.userForm.value.lastName;
        plainUserDto.pseudonym = this.userForm.value.pseudonym === '' ? null : this.userForm.value.pseudonym;
        plainUserDto.usePseudonym =
            this.userForm.value.pseudonym?.length >= 3 ? this.userForm.value.usePseudonym : false;
        plainUserDto.birthdate = this.userForm.value.birthDate ? this.userForm.value.birthDate : null;
        plainUserDto.streetAddress = this.userForm.value.streetAddress;
        plainUserDto.postalCode = this.userForm.value.postalCode;
        plainUserDto.city = this.userForm.value.city;
        plainUserDto.country = this.userForm.value.country;
        const contact = this.userForm.get('contact') as FormGroup;
        if (contact) {
            plainUserDto.email = contact.value.email;
            plainUserDto.phone = contact.value.phone;
            plainUserDto.mobilePhone = contact.value.mobilePhone;
            if (contact.value.company) {
                plainUserDto.company = contact.value.company;
            }
        }
        plainUserDto.gender = this.userForm.value.gender;
        if (this.userForm.value.job) plainUserDto.job = this.userForm.value.job;

        const customProps = this.readCustomPropsFromForm();
        if (customProps?.length > 0) plainUserDto.customPropertyDtos = customProps;
        return plainUserDto;
    }

    toggleEdit(): void {
        this.isEditEnabled = !this.isEditEnabled;
        const customControls = this.customPropertySchemas.flatMap((s) => s.propertySchemas);
        const customControlNames = customControls.flatMap((it) => [
            it.name,
            it.getRepeatValidationCheckBoxFormControlName(),
            it.getRepeatValidationInputFormControlName(),
        ]);
        const permanentlyDisabledControlNames = ['username', 'createdAt', 'patientNumber'];
        for (const controlName of Object.keys(this.userForm.controls).filter(
            (it) => !customControlNames.includes(it) && !permanentlyDisabledControlNames.includes(it),
        )) {
            this.isEditEnabled ? this.userForm.get(controlName).enable() : this.userForm.get(controlName).disable();
        }
        for (const schema of customControls) {
            if (this.mayEditCustomProperty(schema)) {
                const formControlName = schema.name;
                const control = this.userForm.get(formControlName);
                if (control) {
                    if (this.isEditEnabled) {
                        control?.enable();
                        if (schema.validationEnabled) {
                            this.userForm.get(schema.getRepeatValidationCheckBoxFormControlName())?.enable();
                            this.userForm.get(schema.getRepeatValidationInputFormControlName())?.enable();
                        }
                    } else {
                        control?.disable();
                        if (schema.validationEnabled) {
                            this.userForm.get(schema.getRepeatValidationCheckBoxFormControlName())?.disable();
                            this.userForm.get(schema.getRepeatValidationInputFormControlName())?.disable();
                        }
                    }
                }
            }
        }
        const pseudonymInput = this.userForm.get('pseudonym');
        const usePseudonym = this.userForm.get('usePseudonym');
        const hasOtpConfigured = this.userForm.get('hasOtpConfigured');
        const accountType = this.userForm.get('accountType');
        const pseudonymDefinition = this.userForm.get('pseudonymDefinition');
        if (usePseudonym.value) {
            this.isEditEnabled ? pseudonymDefinition.enable() : pseudonymDefinition.disable();
            if (pseudonymDefinition.value) {
                pseudonymInput.disable();
            } else {
                this.isEditEnabled ? pseudonymInput.enable() : pseudonymInput.disable();
            }
        } else {
            pseudonymInput.disable();
            pseudonymDefinition.disable();
        }
        if (this.isEditEnabled && this.user.roles.includes(UserRoles.PATIENT) && this.hasRightToModifyUser()) {
            accountType.enable();
        } else {
            accountType.disable();
        }
        if (
            this.isEditEnabled &&
            this.user.accountType === AccountType.LOGIN_USER &&
            !this.originallyHadOtpConfigured &&
            this.hasRightToModifyUser()
        ) {
            hasOtpConfigured.enable();
        } else {
            hasOtpConfigured.disable();
        }
        this.userForm.markAsPristine();
    }

    async changeGroup(): Promise<void> {
        const selectedGroup: Group[] = [];
        const tableItems: TableItem[] = [
            TableItem.create({
                id: 'selected',
                prop: 'selected',
                header: '',
                adapter: CheckBoxItemAdapterComponent,
                type: ItemType.ADAPTER,
                width: '8%',
                columnPosition: 0,
            }),
            TableItem.create({
                id: 'name',
                prop: 'name',
                header: 'Name',
                type: ItemType.ADAPTER,
                adapter: StringItemAdapterComponent,
                width: '30%',
                columnPosition: 1,
            }),
            TableItem.create({
                id: 'parentName',
                prop: 'parentName',
                header: 'Organisation',
                type: ItemType.ADAPTER,
                adapter: StringItemAdapterComponent,
                width: '30%',
                columnPosition: 2,
            }),
            TableItem.create({
                id: 'category',
                prop: 'category',
                header: 'Kategorie',
                type: ItemType.ADAPTER,
                adapter: StringItemAdapterComponent,
                width: '32%',
                columnPosition: 3,
            }),
        ];
        if (this.groupListConfig.list.items.length && this.groupListConfig.list.items.length > 0) {
            for (const group of this.groupListConfig.list.items) {
                const newGroup = new Group();
                newGroup.uuid = group.uuid;
                selectedGroup.push(newGroup);
            }
        }
        // TODO: Allow more group to select but only one to select
        const modal = await this.modalController.create({
            component: GroupListModalComponent,
            cssClass: 'full-width-modal',
            componentProps: {
                title: 'ORGANISATION_MANAGEMENT.GROUP.CHOOSE',
                selectedGroups: selectedGroup,
                isMultipleChoice: false,
                onlyGroupsWithMembership: true,
                groupLevel: GroupLevel.INSTITUTION,
                tableItems,
                anyItem: 'ORGANISATION_MANAGEMENT.GROUP.ANY_ITEM',
            },
        });
        await modal.present();
        const { data } = await modal.onDidDismiss();
        if (data && data.length > 0) {
            if (!this.isNewUser) {
                try {
                    for (const group of data) {
                        await this.usersService.assignUserToGroup(this.user.username, group.path);
                    }
                    this.toastService.showToast(ToastService.changeSavedMessage, IonicColor.success);
                } catch (err) {
                    this.log.error('Error in changeGroup', err);
                    // If an error is caught while trying to assign user to a group, then do not change the group in the UI list
                    return;
                }
            }
            // Change UI list with the chosen group
            this.userMemberships = [];
            this.groupListConfig.list.items = [];
            for (const group of data) {
                const groupPath = group.path.split('/');
                const groupLevel = this.groupService.getGroupLevel(group.path);
                if (groupLevel === GroupLevel.TENANT || groupLevel === GroupLevel.ORGANIZATION) {
                    group.parentName = '-';
                    if (groupLevel === GroupLevel.TENANT) group.category = GroupCategory.TENANT;
                } else {
                    group.parentName = groupPath[groupPath.length - 2];
                }
                group.titleName = group.parentName === '-' ? `${group.name}` : `${group.name} (${group.parentName})`;
                this.userMemberships.push(group.path);
                this.groupListConfig.list.items.push(group);
            }
            this.groupListConfig.list = PaginatedResponse.init(this.groupListConfig.list.items);
        }
    }

    changePseudonymActivation(value: boolean): void {
        this.userForm.controls.usePseudonym.setValue(value);
        this.userForm.markAsDirty();
        if (value) {
            this.userForm.controls.pseudonymDefinition.enable();
            if (this.userForm.controls.pseudonymDefinition.value) {
                this.userForm.controls.pseudonym.setValue(this.user.patientNumber);
                this.userForm.controls.pseudonym.disable();
                this.userForm.markAsDirty();
            } else {
                this.userForm.controls.pseudonym.enable();
                this.userForm.markAsDirty();
            }
        } else {
            this.userForm.controls.pseudonym.disable();
            this.userForm.controls.pseudonymDefinition.disable();
            this.userForm.markAsDirty();
        }
    }

    changePseudonymDefinition(value: boolean): void {
        this.userForm.controls.pseudonymDefinition.setValue(value);
        if (value) {
            this.userForm.controls.pseudonym.setValue(this.user.patientNumber);
            this.userForm.controls.pseudonym.disable();
            this.userForm.markAsDirty();
        } else {
            this.userForm.controls.pseudonym.enable();
        }
    }

    onChangeAccountType(value: AccountType): void {
        if (!this.isEditEnabled && !this.hasRightToModifyUser()) return;
        this.userForm.patchValue({ accountType: value });
        if (value === AccountType.LOGIN_USER) {
            if (!this.originallyHadOtpConfigured) this.userForm.controls.hasOtpConfigured.enable();
        }
        if (value === AccountType.PLAIN_USER) {
            this.userForm.controls.hasOtpConfigured.disable();
            this.userForm.controls.hasOtpConfigured.patchValue(false);
        }
        this.userForm.markAsDirty();
        this.userForm.controls.hasOtpConfigured.updateValueAndValidity();
    }

    async onChangeOtpRequirement(value: boolean): Promise<void> {
        if (value === true) {
            const modalConfig = new ModalConfig();
            modalConfig.modalTyp = ModalTyp.INFORMATION;
            modalConfig.title = `2FA aktivieren`;
            modalConfig.titleIcon = 'warning-outline';
            modalConfig.description =
                'Wenn Sie die 2FA aktivieren, ist sie für dieses Benutzerkonto nachträglich nicht deaktivierbar. Möchten Sie die 2FA aktivieren?';
            modalConfig.buttonRight = new ButtonConfig();
            modalConfig.buttonRight.buttonText = 'Aktivieren';
            modalConfig.buttonRight.buttonColor = 'primary';
            const action = await this.modalAlertService.showModal(modalConfig);
            if (action?.action !== 'right') {
                this.userForm.controls.hasOtpConfigured.reset(false);
            }
        }
    }

    isProfilePage(): boolean {
        return this.getLocation().includes(RoutingSegment.PROFILE);
    }

    hasRole(role = UserRoles.PATIENT): boolean {
        if (!this.user) return false;
        return this.user?.roles?.includes(role);
    }

    async navigateToDetailPage(userUuid: string): Promise<void> {
        if (this.readUserRoleFromURL() === UserRoles.PATIENT) {
            await this.navigateWithPatientInParams(userUuid);
            return;
        }
        await this.routingService.openUserDetails(this.readUserRoleFromURL(), userUuid, true);
    }

    /**
     * Navigates back to consultation creation, screening creation or order creation based on query params
     * Otherwise navigates to patient detail page
     * @param userUuid -
     */
    async navigateWithPatientInParams(userUuid: string): Promise<void> {
        if (this.isPatientForConsultation) {
            await this.router.navigate([RoutingSegment.MEMBER, RoutingSegment.CONSULTATION_MANAGEMENT, 'new'], {
                queryParams: { patient: userUuid },
            });
        } else if (this.isPatientForScreening) {
            await this.router.navigate([RoutingSegment.MEMBER, RoutingSegment.SCREENING_MANAGEMENT, 'new'], {
                queryParams: { patient: userUuid },
            });
        } else if (this.isPatientForOrder) {
            await this.router.navigate([RoutingSegment.MEMBER, RoutingSegment.ORDER_MANAGEMENT, 'new'], {
                queryParams: { patient: userUuid },
            });
        } else {
            await this.routingService.openUserDetails(this.readUserRoleFromURL(), userUuid, true);
        }
    }

    getLocation(): string {
        // can be longer that router.url, but should always include it
        return decodeURIComponent(this.location.path());
    }

    /** The form holding the files staged for a custom property, created the first time it is asked for. */
    fileFormOfCustomProperty(schema: UserCustomPropertySchema): CurafidaContentInputForm {
        return (this.customPropertyFileForms[schema.uuid] ??= new CurafidaContentInputForm());
    }

    /**
     * The saved files of a custom property, as the edited copy. The list is handed over as it is, never as a new
     * one: `curafida-content-input` takes a removed file off it in place and rebuilds its table on a new list,
     * which would drop the files staged there.
     */
    contentsOfCustomProperty(schema: UserCustomPropertySchema): Content[] | undefined {
        return this.pendingCustomProperties.find((ucp) => ucp.schemaId === schema.uuid)?.contents;
    }

    async openCustomPropertyFile(content: Content): Promise<void> {
        await this.userCustomPropertyService.openUserCustomPropertyFile(this.user.username, content.uuid);
    }

    /** No confirmation, and nothing is written yet, the way a medium is taken off a Neuigkeit: it is deleted on save. */
    stageCustomPropertyFileDeletion(contentUuid: string): void {
        this.fileDeletionQueue.push(contentUuid);
        this.userForm.updateValueAndValidity();
    }

    /** Anyone else's custom properties can be changed, the own ones only as far as the user may write them */
    protected mayEditCustomProperty(schema: UserCustomPropertySchema): boolean {
        return !this.isOwnDetails() || schema.userWritable;
    }

    /**
     * A required file property needs a file, saved or staged, as long as it is shown and the user may change it. The
     * files are no value of its control, so the form checks them, and has to be checked again whenever they change.
     */
    private readonly requiredFilesValidator: ValidatorFn = (): ValidationErrors | null => {
        const propertiesWithoutFiles = this.customPropertySchemas
            .flatMap((group) => group.propertySchemas)
            .filter((schema) => schema.required && schema.isFileProperty() && schema.isVisible(this.userForm))
            .filter((schema) => this.mayEditCustomProperty(schema))
            /* Reads the staged files without making an upload form for a property that has none yet */
            .filter(
                (schema) =>
                    (this.contentsOfCustomProperty(schema)?.length ?? 0) +
                        (this.customPropertyFileForms[schema.uuid]?.files.length ?? 0) ===
                    0,
            )
            .map((schema) => schema.name);
        return propertiesWithoutFiles.length > 0 ? { requiredFiles: propertiesWithoutFiles } : null;
    };

    /** Writes the files the user staged while editing: everything added is uploaded, everything removed is deleted. */
    private async flushCustomPropertyFileChanges(username: string): Promise<void> {
        for (const [schemaUuid, fileForm] of Object.entries(this.customPropertyFileForms)) {
            for (const fileItem of fileForm.files) {
                await lastValueFrom(
                    this.userCustomPropertyService.uploadUserCustomPropertyFile(username, schemaUuid, [fileItem._file]),
                );
                fileForm.remove(fileItem);
            }
        }
        while (this.fileDeletionQueue.length > 0) {
            await lastValueFrom(
                this.userCustomPropertyService.deleteUserCustomPropertyFile$(username, this.fileDeletionQueue[0]),
            );
            this.fileDeletionQueue.shift();
        }
    }

    /** Drops whatever is staged and takes the file lists back to what the backend holds. */
    private resetPendingCustomProperties(): void {
        Object.values(this.customPropertyFileForms).forEach((fileForm) => fileForm.clear());
        this.fileDeletionQueue = [];
        this.pendingCustomProperties = this.copyCustomPropertiesOfUser();
        this.userForm?.updateValueAndValidity();
    }

    /**
     * The properties the user edits are a copy down to the content lists, so that taking a file off the list never
     * reaches `user.customProperties` - the untouched original that cancelling falls back on.
     */
    private copyCustomPropertiesOfUser(): UserCustomProperty[] {
        return (this.user?.customProperties ?? []).map(
            (ucp) => ({ ...ucp, contents: [...(ucp.contents ?? [])] }) as UserCustomProperty,
        );
    }

    onRepeatValidationCheckboxChanged(
        event: { detail: CheckboxChangeEventDetail<boolean> },
        schema: UserCustomPropertySchema,
    ) {
        // Reset repeat validation input to empty string if validation checkbox is unchecked
        if (!event?.detail?.checked) {
            this.userForm.get(schema.getRepeatValidationInputFormControlName()).setValue('');
        }
    }

    public isOtpConfigurationToBeRequired(): boolean {
        return (
            !this.originallyHadOtpConfigured &&
            !!this.userForm.controls.hasOtpConfigured.value &&
            this.hasRightToModifyUser()
        );
    }

    schemaToAnswerOptions(displayOptions: DisplayOptionsSchema) {
        return displayOptions?.options?.displayType === DisplayOptionTypes.SELECT
            ? (displayOptions.options as SelectDisplayOptions).answerOptions.map((v) => new SelectInputItem(v, v)) || []
            : [];
    }

    protected hasRightSeeAndModifiedUserPreferences() {
        return (
            (this.user.roles.includes(UserRoles.PATIENT) &&
                ((this.loggedInUser.roles.includes(UserRoles.SUPERVISOR) &&
                    this.loggedInUser.roles.includes(UserRoles.patient_recruitment)) ||
                    (this.loggedInUser.roles.includes(UserRoles.CAREGIVER) &&
                        this.loggedInUser.roles.includes(UserRoles.patient_recruitment)))) ||
            (!this.user.roles.includes(UserRoles.PATIENT) &&
                this.loggedInUser.roles.includes(UserRoles.USER_MANAGER)) ||
            this.loggedInUser.username === this.user.username
        );
    }

    private async initGroupsConfig() {
        this.groupListConfig.emptyListLabel = 'USER.GROUP.ANY';
        this.groupListConfig.itemSettings = [
            TableItem.create({
                id: 'titleName',
                prop: 'titleName',
                header: 'Name',
                type: ItemType.ADAPTER,
                adapter: StringItemAdapterComponent,
                width: '40%',
            }),
            TableItem.create({
                id: 'name',
                prop: 'name',
                header: 'Name',
                type: ItemType.ADAPTER,
                adapter: StringItemAdapterComponent,
                width: '40%',
                columnPosition: 0,
            }),
            TableItem.create({
                id: 'parentName',
                prop: 'parentName',
                header: 'Organisation',
                type: ItemType.ADAPTER,
                adapter: StringItemAdapterComponent,
                width: '25%',
                columnPosition: 1,
            }),
            TableItem.create({
                id: 'category',
                prop: 'category',
                header: 'Kategorie',
                type: ItemType.ADAPTER,
                adapter: StringItemAdapterComponent,
                width: '35%',
                columnPosition: 2,
            }),
        ];
        await this.initGroupList();
    }

    private async saveNewUser() {
        try {
            const userRoles = [];

            if (this.user.roles.includes(UserRoles.PATIENT)) userRoles.push(UserRoles.PATIENT);
            else {
                for (const role of this.userRoleCardComponent().user().roles) {
                    userRoles.push({ role });
                }
            }

            let newUser: User;
            if (
                this.userForm.value.accountType !== AccountType.LOGIN_USER &&
                this.readUserRoleFromURL() === UserRoles.PATIENT
            ) {
                if (this.loggedInUser.roles.includes(UserRoles.SUPERVISOR)) {
                    newUser = await this.patientAccountService.postSupervisedPassivePatient(
                        this.buildPlainUserDto(),
                        this.userMemberships,
                    );
                } else {
                    newUser = await this.patientAccountService.postPassivePatient(
                        this.buildPlainUserDto(),
                        this.userMemberships,
                    );
                }
            } else {
                if (this.loggedInUser.roles.includes(UserRoles.SUPERVISOR)) {
                    newUser = await this.patientAccountService.postSupervisedLoginPatient(
                        this.buildLoginUserWithoutPasswordDto(),
                        this.userMemberships,
                    );
                } else {
                    /*
                     * postLoginPatient() requires the logged in user to have the role "create_patient"
                     * meanwhile postLoginUser() requires the logged in user to have the role "create_user"
                     * which is why both endpoints are separate from one another.
                     * For more info see ionic-common#7868, curafida-entwicklungsideen#106 and nest-common!346
                     */
                    if (this.readUserRoleFromURL() === UserRoles.PATIENT) {
                        newUser = await this.patientAccountService.postLoginPatient(
                            this.buildLoginUserWithoutPasswordDto(),
                            this.userMemberships,
                            true,
                            this.userForm.controls.hasOtpConfigured.value,
                        );
                    } else {
                        newUser = await this.usersService.postLoginUser(
                            this.buildLoginUserWithoutPasswordDto(),
                            [this.readUserRoleFromURL()],
                            this.userMemberships,
                            true,
                            this.userForm.controls.hasOtpConfigured.value,
                        );
                    }
                }
            }
            this.isNewUser = false;
            await this.flushCustomPropertyFileChanges(newUser.username);
            this.user = await this.usersService.getUser(newUser.username);
            this.emitUser();
            this.refreshPatientDossier();
            this.toastService.showToast(ToastService.changeSavedMessage, IonicColor.success);
            this.loadingService.resetCountLoadingModal();
            await this.navigateToDetailPage(this.user.uuid);
        } catch (e) {
            this.log.error('Error in saveUserInformation', e);
            this.isEditEnabled = true;
            this.isNewUser = true;
            if (e instanceof HttpErrorResponse) await this.handleErrorUserInfo(e);
        }
    }

    private async updateExistingUser() {
        try {
            let updatedUser: User;
            const requireOtpConfiguration = this.isOtpConfigurationToBeRequired();
            if (this.originalAccountType !== this.userForm.get('accountType').value) {
                switch (this.userForm.get('accountType').value) {
                    case AccountType.PLAIN_USER:
                        await this.patientAccountService.downgradeToPlainPatient(this.user.username).toPromise();
                        this.originalAccountType = AccountType.PLAIN_USER;
                        break;
                    case AccountType.LOGIN_USER:
                        await this.patientAccountService
                            .upgradeToLoginPatient(
                                this.user.username,
                                { email: this.userForm.get('contact').value.email },
                                requireOtpConfiguration,
                            )
                            .toPromise();
                        this.originalAccountType = AccountType.LOGIN_USER;
                        break;
                    default:
                        break;
                }
            } else if (requireOtpConfiguration) {
                await this.setRequireOtpConfiguration(this.user.username);
            }
            if (this.user.roles.includes(UserRoles.PATIENT)) {
                updatedUser = await this.patientAccountService.updatePatient(
                    this.buildUpdateUserDto(),
                    this.user.username,
                );
            } else {
                updatedUser = await this.usersService.putUser(this.buildUpdateUserDto(), this.user.username);
            }
            this.updateTitle.emit({
                uuid: updatedUser.uuid,
                firstName: updatedUser.firstname,
                lastName: updatedUser.lastname,
                birthDate: updatedUser.birthdate,
            });
            await this.flushCustomPropertyFileChanges(updatedUser.username);
            this.user = await this.usersService.getUser(updatedUser.username);
            this.emitUser();
            this.resetPendingCustomProperties();
            if (this.userForm.controls.pseudonym) {
                this.userForm.controls.usePseudonym.setValue(this.user.usePseudonym);
            }
            this.toggleEdit();
            this.toastService.showToast(ToastService.changeSavedMessage, IonicColor.success);
            /* Only on the way back to a creation; reopening the record would leave the tab it was edited on */
            if (
                this.readUserRoleFromURL() === UserRoles.PATIENT &&
                this.config().navigateOnEdit &&
                this.returnsToCreation()
            ) {
                await this.navigateWithPatientInParams(updatedUser.uuid);
            }
        } catch (e) {
            this.log.error('Error in saveUserInformation', e);
            if (e instanceof HttpErrorResponse) await this.handleErrorUserInfo(e);
        }
    }

    private emitUser(): void {
        this.emittedUser = this.user;
        this.userChange.emit(this.user);
    }

    /** The values the master data controls show for a user */
    private masterDataOf(user: User) {
        const pseudonym = user?.pseudonym;
        return {
            pvsId: user?.pvsId || '',
            title: user?.title || '',
            firstName: user?.firstname || '',
            lastName: user?.lastname || '',
            birthDate: user?.birthdate,
            pseudonym,
            usePseudonym: pseudonym?.length >= 3 ? user.usePseudonym : false,
            gender: user?.gender,
            streetAddress: user?.streetAddress || '',
            postalCode: user?.postalCode || '',
            city: user?.city || '',
            country: user?.country || '',
            job: user?.job || '',
            createdAt:
                user?.created_at == null
                    ? format(new Date(), 'dd.MM.yyyy')
                    : format(parseISO(user.created_at), 'dd.MM.yyyy'),
            patientNumber: user?.patientNumber ? user.patientNumber : null,
            pseudonymDefinition: user?.pseudonym === user?.patientNumber?.toString() || user?.pseudonym === '',
        };
    }

    /** The values the controls of a custom property show for a user */
    private customPropertyValuesOf(schema: UserCustomPropertySchema, user: User) {
        const customProp = user?.customProperties?.find((p) => p.schemaId === schema.uuid);
        return {
            value: customProp?.value || schema.displayOptions?.options?.defaultValue || '',
            validated: customProp?.validated || false,
            validatedValue: customProp?.validated ? customProp?.value : '',
        };
    }

    /** Fills the form with the master data and custom properties of a user handed over anew */
    private showMasterDataOf(user: User): void {
        const values: Record<string, unknown> = { ...this.masterDataOf(user) };
        if (user?.accountType) {
            values.accountType = user.accountType;
            this.originalAccountType = user.accountType;
        }
        for (const schema of this.customPropertySchemas.flatMap((group) => group.propertySchemas ?? [])) {
            const { value, validated, validatedValue } = this.customPropertyValuesOf(schema, user);
            values[schema.name] = value;
            if (schema.validationEnabled) {
                values[schema.getRepeatValidationCheckBoxFormControlName()] = validated;
                values[schema.getRepeatValidationInputFormControlName()] = validatedValue;
            }
        }
        this.pendingCustomProperties = this.copyCustomPropertiesOfUser();
        this.userForm.patchValue(values);
        this.userFormBackup?.create();
    }

    private readUserRoleFromURL(): UserRoles {
        return this.usersService.readRoleFromUrl(this.getLocation());
    }

    /** Whether the patient was opened from a consultation, screening or order being created, to return there */
    private returnsToCreation(): boolean {
        return this.isPatientForConsultation || this.isPatientForScreening || this.isPatientForOrder;
    }

    private async initCustomPropsFormControls() {
        this.pendingCustomProperties = this.copyCustomPropertiesOfUser();
        /* The endpoint rejects the whole request for a single role it does not know */
        const roles = this.isProfilePage()
            ? mainUserRoles.filter((role) => this.user.roles.includes(role))
            : [this.usersService.readRoleFromUrl(this.getLocation())];
        // TODO: change if group changes when registering a new user
        const schemas = await lastValueFrom(
            this.usersService.getUserCustomPropertySchemasByGroup(this.loggedInUser.groups[0], roles),
        );
        this.customPropertySchemas = schemas?.filter((s) => !s.propertyGroupUuid) || [];
        if (this.viewerIsPatient() || this.isOwnDetails()) {
            this.customPropertySchemas = this.customPropertySchemas?.filter((s) => s.userReadable) || [];
        }
        for (const customPropertySchema of this.customPropertySchemas) {
            customPropertySchema.propertySchemas = schemas.filter(
                (s) => s?.propertyGroupUuid === customPropertySchema?.uuid,
            );
        }

        for (const propertyGroup of this.customPropertySchemas) {
            for (const schema of propertyGroup.propertySchemas) {
                const {
                    value: initialValue,
                    validated,
                    validatedValue,
                } = this.customPropertyValuesOf(schema, this.user);
                const customPropertyFormControlName = schema.name;
                this.userForm.addControl(
                    customPropertyFormControlName,
                    new FormControl(
                        {
                            value: initialValue,
                            disabled: !this.isEditEnabled,
                        },
                        schema.required && !schema.isFileProperty() ? requiredValidators : null,
                    ),
                );
                if (schema.validationEnabled) {
                    const validationRepeatCheckboxFormControlName = schema.getRepeatValidationCheckBoxFormControlName();
                    this.userForm.addControl(
                        validationRepeatCheckboxFormControlName,
                        new FormControl({
                            value: validated,
                            disabled: !this.isEditEnabled,
                        }),
                    );
                    const validationRepeatInputFormControlName = schema.getRepeatValidationInputFormControlName();
                    this.userForm.addControl(
                        validationRepeatInputFormControlName,
                        new FormControl({
                            value: validatedValue,
                            disabled: !this.isEditEnabled,
                        }),
                    );
                    this.userForm.addValidators(repeatValidationValidator(schema));
                }
            }
        }
        this.userForm.addValidators(this.requiredFilesValidator);
        this.watchCustomPropertyVisibility();
    }

    /* Started after all custom property controls exist, since a display condition may refer to
     * a control added later in the loop above. Replaces the previous watchers on re-initialisation. */
    private watchCustomPropertyVisibility(): void {
        this.customPropertyVisibilitySubscription?.unsubscribe();
        this.customPropertyVisibilitySubscription = new Subscription();
        for (const schema of this.customPropertySchemas.flatMap((group) => group.propertySchemas)) {
            const watcher = schema.watchVisibility(this.userForm);
            if (watcher) this.customPropertyVisibilitySubscription.add(watcher);
        }
    }

    private viewerIsPatient(): boolean {
        return this.loggedInUser.roles.includes(UserRoles.PATIENT);
    }

    private readCustomPropsFromForm(): UserCustomPropertyDto[] {
        const customProps = [];
        let schemas = this.customPropertySchemas?.flatMap((s) => s.propertySchemas) || [];
        if (this.viewerIsPatient() || this.isOwnDetails()) {
            schemas = schemas.filter((s) => s.userWritable);
        }
        for (const schema of schemas) {
            const formControlName = schema.name;
            const userCustomPropertyDto = new UserCustomPropertyDto(
                schema.uuid,
                this.userForm.controls[formControlName].value,
            );
            if (userCustomPropertyDto.value !== undefined) {
                if (schema.validationEnabled) {
                    const validationCheckboxFormControl = this.userForm.get(
                        schema.getRepeatValidationCheckBoxFormControlName(),
                    );
                    const validationInputFormControl = this.userForm.get(
                        schema.getRepeatValidationInputFormControlName(),
                    );
                    userCustomPropertyDto.validated =
                        (schema.validationRequired || validationCheckboxFormControl.getRawValue() === true) &&
                        userCustomPropertyDto.value === validationInputFormControl?.value;
                }
                // if this is an update, value existed beforehand and had an uuid
                if (this.user?.customProperties?.length > 0) {
                    const uuid = this.user.customProperties.find((p) => p.schemaId === schema.uuid)?.uuid;
                    if (uuid) userCustomPropertyDto.uuid = uuid;
                }
                // only save relevant changes (modified data) to backend
                if (!userCustomPropertyDto.isPristine()) customProps.push(userCustomPropertyDto);
            }
        }
        return customProps;
    }

    private async handleErrorUserInfo(e: HttpErrorResponse) {
        let errorMsg: string;
        if (e?.error?.message?.length) errorMsg = e.error?.message;
        if (errorMsg?.includes('Pseudonym')) {
            this.toastService.showToast('USER.ERROR.PSEUDONYM', IonicColor.danger);
        } else if (errorMsg?.includes('Email')) {
            this.toastService.showToast(
                this.translateService.instant('USER.ERROR.EMAIL_TAKEN', {
                    email: this.userForm.get('contact').value.email,
                }),
                IonicColor.danger,
            );
        } else {
            this.log.error('[handleErrorUserInfo] error');
            await this.ngOnInit();
        }
    }
}
